import { Injectable, inject } from '@angular/core';
import { BaseApiService, PaginatedResults } from '@bo/ng-api';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import {
  Transaction,
  TransactionCategory,
  TransactionCategoryNameRequest,
  TransactionCategoryRequest,
  TransactionCategoryVisibilityRequest,
  TransactionReady,
  TransactionsSendRemindersRequest,
  TransactionStatus,
  TransactionUpdateRequest,
} from '../../_interfaces/transactions.interface';
import { Params } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class TransactionsApiService extends BaseApiService {
  protected override http: HttpClient;

  override baseUrl: string = environment.apiRoot;
  URL_TRANSACTIONS: string = 'transactions';
  URL_TRANSACTIONS_CATEGORY: string = 'transactions/category';
  URL_TRANSACTIONS_EXPORT: string = 'transactions/export';
  URL_TRANSACTIONS_REMINDERS: string = 'transactions/reminders';

  // Relative to URL_TRANSACTIONS
  URL_READY: string = 'ready';
  URL_UNMARK: string = 'unmark';

  constructor() {
    const http = inject(HttpClient);
    super(http);
    this.http = http;
  }

  /**
   * Get all transactions - Paginated
   * @return Observable<PaginatedResults<Transaction>>
   */
  getTransactions(queryParams: HttpParams): Observable<PaginatedResults<Transaction>> {
    const url = this.getUrl(this.URL_TRANSACTIONS, true);
    return this.http.get<Transaction>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Transaction>(res);
      }),
    );
  }

  /**
   * Get all transactions with a ready status
   * @return Observable<Transaction[]>
   */
  //TODO: revisit the implementation of this method. AFAIK only max 100 will be returned if no pagination params are set, which might affect the intended functionality
  getReadyTransactions(): Observable<Transaction[]> {
    const params = new HttpParams().append('status', TransactionStatus.READY);
    const url = this.getUrl(this.URL_TRANSACTIONS, true);
    return this.http.get<Transaction[]>(url, { params: params });
  }

  /**
   * Get a transaction by ID
   *
   * @param transactionId
   * @return Observable<Transaction>
   */
  getTransactionById(transactionId: string) {
    const url = this.getUrl([this.URL_TRANSACTIONS, transactionId], true);
    return this.http.get<Transaction>(url);
  }

  /**
   * Mark transaction as ready
   *
   * @param transactionId
   * @return Observable<TransactionReady>
   */
  markTransactionReady(transactionId: string): Observable<TransactionReady> {
    const url = this.getUrl([this.URL_TRANSACTIONS, transactionId, this.URL_READY], true);
    return this.http.post<TransactionReady>(url, {});
  }

  /**
   * Unmark transaction as ready
   *
   * @param transactionId
   * @return Observable<TransactionReady>
   */
  unmarkTransactionReady(transactionId: string): Observable<TransactionReady> {
    const url = this.getUrl([this.URL_TRANSACTIONS, transactionId, this.URL_UNMARK], true);
    return this.http.post<TransactionReady>(url, {});
  }

  /**
   * Update transaction details
   *
   * @param id
   * @param data
   * @return Observable<Transaction>
   */
  updateTransaction(id: string, data: TransactionUpdateRequest): Observable<Transaction> {
    const url = this.getUrl([this.URL_TRANSACTIONS, id], true);
    return this.http.patch<Transaction>(url, data);
  }

  exportTransactions(exportParams: Params) {
    const url = this.getUrl([this.URL_TRANSACTIONS_EXPORT], true);
    return this.http.post(url, exportParams);
  }

  /**
   * Get paginated transaction categories
   *
   * @return Observable<TransactionCategory>
   */
  getTransactionCategoriesPaginated(queryParams: HttpParams): Observable<PaginatedResults<TransactionCategory>> {
    // Default to hidden=false unless the caller (e.g. categories toggle) already set it
    if (!queryParams.has('hidden')) {
      queryParams = queryParams.set('hidden', 'false');
    }
    const url = this.getUrl(this.URL_TRANSACTIONS_CATEGORY, true);
    return this.http.get<TransactionCategory>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<TransactionCategory>(res);
      }),
    );
  }

  /**
   * Get all transaction categories
   *
   * @return Observable<TransactionCategory>
   */
  getTransactionCategoriesList(): Observable<TransactionCategory[]> {
    const url = this.getUrl(this.URL_TRANSACTIONS_CATEGORY, true);
    return this.http.get<TransactionCategory[]>(url);
  }

  /**
   * Get transaction category by ID
   *
   * @return Observable<TransactionCategory>
   */
  getTransactionCategoryById(categoryId: number): Observable<TransactionCategory> {
    const url = this.getUrl([this.URL_TRANSACTIONS_CATEGORY, categoryId], true);
    return this.http.get<TransactionCategory>(url);
  }

  /**
   * Create new transaction category
   *
   * @param data
   * @return Observable<TransactionCategory>
   */
  createTransactionCategory(data: TransactionCategoryRequest): Observable<TransactionCategory> {
    const url = this.getUrl(this.URL_TRANSACTIONS_CATEGORY, true);
    return this.http.post<TransactionCategory>(url, data);
  }

  /**
   * Update an existing transaction category name only
   *
   * @param data
   * @return Observable<TransactionCategory>
   */
  editTransactionCategoryName(data: TransactionCategoryNameRequest): Observable<TransactionCategory> {
    const url = this.getUrl([this.URL_TRANSACTIONS_CATEGORY, data.id], true);
    return this.http.patch<TransactionCategory>(url, data);
  }

  /**
   * Update an existing transaction category visibility only
   *
   * @param data
   * @return Observable<TransactionCategory>
   */
  editTransactionCategoryVisibility(data: TransactionCategoryVisibilityRequest): Observable<TransactionCategory> {
    const url = this.getUrl([this.URL_TRANSACTIONS_CATEGORY, data.id], true);
    return this.http.patch<TransactionCategory>(url, data);
  }

  /**
   * Remove transaction category
   *
   * @param categoryId
   * @return Observable<Record<PropertyKey, never>>
   */
  removeTransactionCategory(categoryId: number): Observable<Record<PropertyKey, never>> {
    const url = this.getUrl([this.URL_TRANSACTIONS_CATEGORY, categoryId], true);
    return this.http.delete<Record<PropertyKey, never>>(url);
  }

  /**
   * Send transaction reminders to business employees
   *
   * @param bizId
   * @return Observable<TransactionsSendRemindersRequest>
   */
  sendTransactionsReminders(bizId: string): Observable<TransactionsSendRemindersRequest> {
    const url = this.getUrl(this.URL_TRANSACTIONS_REMINDERS, true);
    return this.http.post<TransactionsSendRemindersRequest>(url, { biz_id: bizId });
  }
}
